import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getChatRadii, getChatBorders } from '../chatStyles';
import {
  CheckIcon,
  DescriptionIcon,
  SlideshowIcon,
  TableChartIcon,
  WarningIcon
} from '../../../../shared/icons';

const APP_CONFIG = {
  excel: {
    label: 'Excel',
    fileLabel: 'workbook',
    color: '#217346',
    icon: TableChartIcon
  },
  powerpoint: {
    label: 'PowerPoint',
    fileLabel: 'presentation',
    color: '#D24726',
    icon: SlideshowIcon
  },
  word: {
    label: 'Word',
    fileLabel: 'document',
    color: '#2B579A',
    icon: DescriptionIcon
  }
};

const StepStatusIcon = ({ status, color }) => {
  if (status === 'completed') {
    return <CheckIcon sx={{ fontSize: 14, color }} />;
  }
  if (status === 'failed') {
    return <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />;
  }
  return <CircularProgress size={11} thickness={5} sx={{ color }} />;
};

const ConnectedAppWork = ({ data = {} }) => {
  const app = APP_CONFIG[data.app] ? data.app : 'excel';
  const config = APP_CONFIG[app];
  const AppIcon = config.icon;
  const steps = Array.isArray(data.steps) ? data.steps : [];
  const hasError = data.hasError === true || steps.some(step => step.status === 'failed');
  const isActive = data.isLoading === true || steps.some(step => step.status === 'in_progress');
  const heading = hasError
    ? `${config.label} tool needs attention`
    : (isActive ? `Using ${config.label}` : `Used ${config.label}`);
  const visibleSteps = steps.slice(-4);
  const hiddenStepCount = Math.max(steps.length - visibleSteps.length, 0);

  return (
    <Box
      role="group"
      aria-label={`${config.label} tool activity`}
      aria-busy={isActive}
      sx={(theme) => ({
        width: '100%',
        boxSizing: 'border-box',
        border: getChatBorders(theme).resting,
        borderRadius: getChatRadii(theme).card,
        backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.035 : 0.018),
        px: 1.25,
        py: 0.9,
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Box
          sx={(theme) => ({
            width: 20,
            height: 20,
            borderRadius: getChatRadii(theme).chip,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            color: config.color,
            backgroundColor: alpha(config.color, 0.1)
          })}
        >
          <AppIcon sx={{ fontSize: 13 }} />
        </Box>
        <Typography
          variant="body2"
          sx={{
            minWidth: 0,
            flex: 1,
            fontWeight: 500,
            fontSize: theme => theme.typography.pxToRem(12),
            lineHeight: 1.35,
            color: hasError ? 'error.main' : 'text.secondary',
          }}
        >
          {heading}
        </Typography>
      </Box>

      {steps.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.55, mt: 0.75, pl: 0.25 }}>
          {hiddenStepCount > 0 && (
            <Typography variant="caption" sx={{ color: 'text.disabled', lineHeight: 1.35, pl: 2.6 }}>
              {hiddenStepCount} earlier {hiddenStepCount === 1 ? 'action' : 'actions'}
            </Typography>
          )}
          {visibleSteps.map((step, index) => (
            <Box
              key={step.toolId || `${step.toolName || 'step'}-${index}`}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}
            >
              <Box sx={{ width: 14, height: 18, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <StepStatusIcon status={step.status} color={config.color} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: step.status === 'failed' ? 'error.main' : 'text.secondary',
                    lineHeight: 1.45,
                    overflowWrap: 'anywhere'
                  }}
                >
                  {step.label || 'Updating the file'}
                </Typography>
                {step.status === 'failed' && step.error && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'error.main', opacity: 0.86, lineHeight: 1.35 }}
                  >
                    {step.error}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {steps.length === 0 && isActive && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.6, pl: 3.5, color: 'text.secondary' }}>
          Preparing the {config.fileLabel}
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(ConnectedAppWork);
